/**
 * Optional page-level metadata overrides (title/description) layered on the
 * route catalog. Pages call useSetPageMeta; the document sync reads overrides
 * from context so client navigation never leaves stale values behind.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { PageMetaOverride } from './types';

interface PageMetaContextValue {
  override: PageMetaOverride | null;
  setOverride: (next: PageMetaOverride | null) => void;
}

const PageMetaContext = createContext<PageMetaContextValue | null>(null);

export function PageMetaProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [override, setOverrideState] = useState<PageMetaOverride | null>(null);
  const setOverride = useCallback((next: PageMetaOverride | null) => {
    setOverrideState(next);
  }, []);
  const value = useMemo(() => ({ override, setOverride }), [override, setOverride]);
  return <PageMetaContext.Provider value={value}>{children}</PageMetaContext.Provider>;
}

export function usePageMetaOverride(): PageMetaOverride | null {
  return useContext(PageMetaContext)?.override ?? null;
}

/**
 * Declare page-specific title/description for the lifetime of the caller.
 * Clears on unmount so the route catalog takes over again.
 *
 * @example
 * useSetPageMeta({ title: 'Welcome series', description: 'Edit this automation flow.' });
 */
export function useSetPageMeta(override: PageMetaOverride | null): void {
  const ctx = useContext(PageMetaContext);
  const title = override?.title;
  const description = override?.description;
  const indexable = override?.indexable;
  const canonicalPath = override?.canonicalPath;
  const hasOverride = override != null;

  useEffect(() => {
    if (!ctx) return;
    if (!hasOverride) {
      ctx.setOverride(null);
      return;
    }
    ctx.setOverride({ title, description, indexable, canonicalPath });
    return () => {
      ctx.setOverride(null);
    };
  }, [ctx, hasOverride, title, description, indexable, canonicalPath]);
}
